import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import 'mantine-react-table/styles.css'
import './global.css'

import { StrictMode, useEffect, useMemo } from 'react'
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser'
import { MsalProvider, useMsal } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ErrorPage } from '@shared/components/error-page/error-page'
import { Router } from './routing/router'
import { theme } from './theme'
import { UserContextProvider } from './providers/user-context'
import { configService } from '@/services/config-service'
import ReactGA from 'react-ga4'
import Clarity from '@microsoft/clarity'

type AppProps = { msalInstance: PublicClientApplication }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

export default function App({ msalInstance }: AppProps) {
  useEffect(() => {
    const gaTagId = configService().get('GOOGLE_ANALYTICS_TAG_ID')
    if (typeof gaTagId === 'string' && gaTagId) {
      ReactGA.initialize(gaTagId)
    }
  }, [])

  useEffect(() => {
    const microsoftClarityId = configService().get('MICROSOFT_CLARITY_ID')
    if (typeof microsoftClarityId === 'string' && microsoftClarityId) {
      Clarity.init(microsoftClarityId)
    }
  }, [])

  // Identify the user in Microsoft Clarity
  const { instance } = useMsal()
  const account: AccountInfo | null = useMemo(() => instance.getActiveAccount(), [instance])
  useEffect(() => {
    if (account) {
      Clarity.identify(account.homeAccountId, undefined, undefined, account.name)
    }
  }, [account])

  return (
    <StrictMode>
      <ErrorBoundary fallbackRender={ErrorPage}>
        <MantineProvider theme={theme}>
          <MsalProvider instance={msalInstance}>
            <UserContextProvider>
              <QueryClientProvider client={queryClient}>
                <Router />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </UserContextProvider>
          </MsalProvider>
          <Notifications />
        </MantineProvider>
      </ErrorBoundary>
    </StrictMode>
  )
}
